import { useQuery } from "@tanstack/react-query";
import { getChangeLogByUPN } from "api";
import { useLanguage } from "contexts/LanguageContext";
import {
  DataChange,
  EmployeeChangeLogEntry,
  EmployeeChangeLogResponseEntry,
  LabeledDataChange,
} from "types/Employee";
import { translateLabel } from "../utils/helpers";

const sortLogEntryByDateDesc = (
  log1: EmployeeChangeLogEntry,
  log2: EmployeeChangeLogEntry
): number => {
  return (
    new Date(log2.modifiedAt).getTime() - new Date(log1.modifiedAt).getTime()
  );
};

const sortChangesAphabetically =
  (lang: string, messages: Record<string, string | Record<string, string>>) =>
  (c1: LabeledDataChange, c2: LabeledDataChange) => {
    const label1 = translateLabel(c1.label, messages);
    const label2 = translateLabel(c2.label, messages);
    if (c1.label === "reason") {
      return 1; // 'reason' should always be last
    }
    if (c2.label === "reason") {
      return -1; // 'reason' should always be last
    }
    if (c1.label === "approver") {
      return 1; // 'approver' should always be second last
    }
    if (c2.label === "approver") {
      return -1; // 'approver' should always be second last
    }
    return label1.localeCompare(label2, lang, {
      sensitivity: "base",
    });
  };

const transformer = (
  input: EmployeeChangeLogResponseEntry[]
): EmployeeChangeLogEntry[] => {
  return input.map((item) => {
    return {
      modifiedAt: item.modifiedAt,
      modifiedBy: item.modifiedBy,
      currency: item.currency,
      changes: Object.entries(item.changes).map(([key, value]) => {
        const v =
          Object.entries(value).length > 0
            ? (value as DataChange)
            : { old: undefined, new: undefined };
        return {
          label: key,
          ...v,
        };
      }),
    };
  });
};

export const useEmployeeChangeLog = (employeeUPN: string, headers: Headers) => {
  const { language, messages } = useLanguage();
  const queryResult = useQuery({
    queryKey: ["employeeDetail", employeeUPN],
    queryFn: () => getChangeLogByUPN(String(employeeUPN), headers),
    select: (input) =>
      transformer(input)
        .map((item) => ({
          ...item,
          changes: item.changes.sort(
            sortChangesAphabetically(language, messages)
          ),
        }))
        .toSorted(sortLogEntryByDateDesc),
  });

  let error;
  if (queryResult.error instanceof Error) {
    error = queryResult.error as Error;
  } else {
    error = new Error("Unkown error", { cause: error });
  }

  return {
    changeLog: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: error,
  };
};
